/**
 * Webhook Details Modal (September 2025)
 *
 * Modal component for viewing webhook configuration and delivery statistics.
 *
 * Features:
 * - Comprehensive webhook configuration display
 * - Delivery statistics and success rate
 * - Last delivery details with error messages
 * - Event subscription list
 * - Terminal-themed styling with Tailwind CSS
 */

import type { Id } from '../../convex/_generated/dataModel';
import type { WebhookEventType } from '@keypalace/shared-types';

interface Webhook {
  id: Id<'webhooks'>;
  name: string;
  url: string;
  status: 'enabled' | 'disabled';
  events: WebhookEventType[];
  retryConfig: {
    maxRetries: number;
    backoffMultiplier: number;
    initialDelayMs: number;
  };
  customHeaders?: Record<string, string>;
  description?: string;
  lastDeliveryAt?: number;
  lastDeliveryStatus?: 'success' | 'failure';
  lastErrorMessage?: string;
  successCount: number;
  failureCount: number;
  createdAt: number;
  updatedAt: number;
}

interface WebhookDetailsModalProps {
  isOpen: boolean;
  webhook: Webhook | null;
  onClose: () => void;
  onEdit?: () => void;
  onRotateSecret?: () => void;
}

const EVENT_LABELS: Record<WebhookEventType, string> = {
  'service_key.rotated': 'Service Key Rotated',
  'service_key.created': 'Service Key Created',
  'child_key.created': 'Child Key Created',
  'child_key.revoked': 'Child Key Revoked',
  'credits.low_threshold': 'Credits Low Threshold',
  'credits.exhausted': 'Credits Exhausted',
};

export function WebhookDetailsModal({
  isOpen,
  webhook,
  onClose,
  onEdit,
  onRotateSecret,
}: WebhookDetailsModalProps) {
  if (!isOpen || !webhook) return null;

  const totalDeliveries = webhook.successCount + webhook.failureCount;
  const successRate =
    totalDeliveries > 0
      ? Math.round((webhook.successCount / totalDeliveries) * 100)
      : 0;

  const formatRelativeTime = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-terminal-black/90 backdrop-blur-sm">
      <div className="terminal-box max-w-4xl w-full mx-4 p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-terminal-orange terminal-glow">
            [WEBHOOK DETAILS]
          </h2>
          <button
            onClick={onClose}
            className="text-terminal-cyan hover:text-terminal-orange text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Webhook Configuration */}
          <div className="bg-terminal-black border-2 border-terminal-cyan p-6">
            <h3 className="text-terminal-orange font-bold text-lg mb-4">
              [CONFIGURATION]
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                  Name
                </label>
                <div className="text-terminal-cyan text-lg font-semibold">
                  {webhook.name}
                </div>
              </div>
              <div>
                <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                  Status
                </label>
                <div>
                  <span
                    className={`inline-block px-3 py-1 text-xs font-bold border-2 ${
                      webhook.status === 'enabled'
                        ? 'text-green-400 border-green-400'
                        : 'text-red-400 border-red-400'
                    }`}
                  >
                    [{webhook.status.toUpperCase()}]
                  </span>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                  Endpoint URL
                </label>
                <div className="text-terminal-cyan text-sm font-mono break-all">
                  {webhook.url}
                </div>
              </div>
              {webhook.description && (
                <div className="md:col-span-2">
                  <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                    Description
                  </label>
                  <div className="text-terminal-cyan text-sm">
                    {webhook.description}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Delivery Statistics */}
          <div className="bg-terminal-black border-2 border-terminal-cyan p-6">
            <h3 className="text-terminal-orange font-bold text-lg mb-4">
              [DELIVERY STATISTICS]
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-terminal-cyan/70 text-xs font-bold uppercase mb-1">
                  Total Deliveries
                </div>
                <div className="text-3xl font-bold text-terminal-orange terminal-glow">
                  {totalDeliveries}
                </div>
              </div>
              <div className="text-center">
                <div className="text-terminal-cyan/70 text-xs font-bold uppercase mb-1">
                  Successful
                </div>
                <div className="text-3xl font-bold text-green-400">
                  {webhook.successCount}
                </div>
              </div>
              <div className="text-center">
                <div className="text-terminal-cyan/70 text-xs font-bold uppercase mb-1">
                  Failed
                </div>
                <div className="text-3xl font-bold text-red-400">
                  {webhook.failureCount}
                </div>
              </div>
              <div className="text-center">
                <div className="text-terminal-cyan/70 text-xs font-bold uppercase mb-1">
                  Success Rate
                </div>
                <div
                  className={`text-3xl font-bold ${
                    successRate >= 95
                      ? 'text-green-400'
                      : successRate >= 80
                        ? 'text-terminal-orange'
                        : 'text-red-400'
                  }`}
                >
                  {successRate}%
                </div>
              </div>
            </div>

            {webhook.lastDeliveryAt && (
              <div className="mt-6 pt-6 border-t border-terminal-cyan/30">
                <h4 className="text-terminal-cyan font-bold mb-3">
                  LAST DELIVERY
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                      Timestamp
                    </label>
                    <div className="text-terminal-cyan text-sm">
                      {formatDate(webhook.lastDeliveryAt)}
                    </div>
                    <div className="text-terminal-cyan/50 text-xs">
                      ({formatRelativeTime(webhook.lastDeliveryAt)})
                    </div>
                  </div>
                  <div>
                    <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                      Status
                    </label>
                    <div>
                      <span
                        className={`inline-block px-3 py-1 text-xs font-bold border-2 ${
                          webhook.lastDeliveryStatus === 'success'
                            ? 'text-green-400 border-green-400'
                            : 'text-red-400 border-red-400'
                        }`}
                      >
                        [{webhook.lastDeliveryStatus?.toUpperCase() || 'UNKNOWN'}]
                      </span>
                    </div>
                  </div>
                  {webhook.lastErrorMessage && (
                    <div className="md:col-span-3">
                      <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                        Error Message
                      </label>
                      <div className="text-red-400 text-sm font-mono bg-red-900/20 p-3 border border-red-400/30">
                        {webhook.lastErrorMessage}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Event Subscriptions */}
          <div className="bg-terminal-black border-2 border-terminal-cyan p-6">
            <h3 className="text-terminal-orange font-bold text-lg mb-4">
              [EVENT SUBSCRIPTIONS] ({webhook.events.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {webhook.events.map((event) => (
                <div
                  key={event}
                  className="flex items-center gap-2 text-terminal-cyan text-sm p-2 bg-terminal-black border border-terminal-cyan/30"
                >
                  <span className="text-terminal-orange">●</span>
                  {EVENT_LABELS[event]}
                </div>
              ))}
            </div>
          </div>

          {/* Retry Configuration */}
          <div className="bg-terminal-black border-2 border-terminal-cyan p-6">
            <h3 className="text-terminal-orange font-bold text-lg mb-4">
              [RETRY CONFIGURATION]
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                  Max Retries
                </label>
                <div className="text-terminal-cyan text-lg font-semibold">
                  {webhook.retryConfig.maxRetries}
                </div>
              </div>
              <div>
                <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                  Backoff Multiplier
                </label>
                <div className="text-terminal-cyan text-lg font-semibold">
                  {webhook.retryConfig.backoffMultiplier}x
                </div>
              </div>
              <div>
                <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                  Initial Delay
                </label>
                <div className="text-terminal-cyan text-lg font-semibold">
                  {webhook.retryConfig.initialDelayMs}ms
                </div>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-terminal-black border-2 border-terminal-cyan p-6">
            <h3 className="text-terminal-orange font-bold text-lg mb-4">
              [METADATA]
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                  Created
                </label>
                <div className="text-terminal-cyan text-sm">
                  {formatDate(webhook.createdAt)}
                </div>
              </div>
              <div>
                <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                  Last Updated
                </label>
                <div className="text-terminal-cyan text-sm">
                  {formatDate(webhook.updatedAt)}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 bg-transparent text-terminal-cyan font-bold py-3 px-6 border-2 border-terminal-cyan hover:bg-terminal-cyan hover:text-terminal-black transition-all duration-300"
            >
              CLOSE
            </button>
            {onEdit && (
              <button
                onClick={() => {
                  onClose();
                  onEdit();
                }}
                className="flex-1 bg-terminal-orange text-terminal-black font-bold py-3 px-6 border-2 border-terminal-orange hover:bg-transparent hover:text-terminal-orange transition-all duration-300 terminal-glow"
              >
                EDIT WEBHOOK
              </button>
            )}
            {onRotateSecret && (
              <button
                onClick={() => {
                  onClose();
                  onRotateSecret();
                }}
                className="flex-1 bg-terminal-cyan text-terminal-black font-bold py-3 px-6 border-2 border-terminal-cyan hover:bg-transparent hover:text-terminal-cyan transition-all duration-300"
              >
                ROTATE SECRET
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
